/**
 * Shared vocabulary for "an authenticated session ended", used by both the web
 * app and mobile.
 *
 * Pure logic only — the stateful bookkeeping lives elsewhere and is
 * deliberately not shared: web classifies at the call site
 * (`./session-telemetry`), mobile defers classification behind a pending mark
 * with a TTL (`apps/mobile/src/lib/sentry.ts`).
 */

/**
 * What specifically triggered the session end. Expected to grow (e.g.
 * `admin_revoked`, `password_reset_revocation`) as server-side attribution
 * lands.
 */
export type SessionEndCause =
  /** The user tapped/clicked sign out. */
  | 'user_action'
  /** Mobile: a 401 was confirmed against the server as a real expiry. */
  | 'verified_expiry'
  /** Web: the session query resolved with no user. */
  | 'session_absence_confirmed'
  /** Web: an authenticated route guard found no session. */
  | 'route_guard_no_session'
  /**
   * Mobile: the session ended without a fresh pending mark — no sign-out and
   * no verified expiry preceded it (see `captureMobileSessionEnded`). Counted
   * as forced: the user did not choose to leave.
   */
  | 'unclassified';

const FORCED_LOGOUT_CAUSES = new Set<SessionEndCause>([
  'verified_expiry',
  'session_absence_confirmed',
  'route_guard_no_session',
  'unclassified',
]);

/**
 * The distinction that matters to the product: did the user choose to leave,
 * or were they logged out? Both paths run the same teardown, so without this
 * bit forced logouts are invisible in the numbers. Always derived from the
 * cause — events carry `{ cause, forced }` and a hand-written pair can't
 * disagree. Dashboards group on `forced`, which never changes shape as causes
 * are added.
 */
export function isForcedLogoutCause(cause: SessionEndCause): boolean {
  return FORCED_LOGOUT_CAUSES.has(cause);
}

export type ForcedLogoutReportInput = {
  cause: SessionEndCause;
  /**
   * Whether this client had evidence of a session before the end. Guards
   * against counting first-time visitors who simply opened an authenticated
   * URL — they trip the same route guard as a user who was kicked out.
   */
  hadKnownSession: boolean;
  /**
   * Whether a forced logout was already reported for this teardown. The route
   * guard and the session-absence effect can both fire for one underlying
   * event; without this they double-count.
   */
  alreadyReported: boolean;
};

export function shouldReportForcedLogout({
  alreadyReported,
  cause,
  hadKnownSession,
}: ForcedLogoutReportInput): boolean {
  if (!isForcedLogoutCause(cause)) return false;
  if (!hadKnownSession) return false;
  return !alreadyReported;
}
